import { isProduct } from "./guards"
import {
    Interval,
    MediaValue,
    NumberValue,
    PossibleValue,
    Product,
    ProductResult
} from "./types/response"

/**
 * Returns the value of a characteristic of a product.
 * @param {Object} product the product structure
 * @return {String} the name of the product or null
 */
export function nameOf(
    product: Product | ProductResult
): string | null | undefined {
    if (!product) {
        return null
    }
    if (isProduct(product)) {
        return product.name
    }
    if (product.product) {
        return product.product.name || null
    } else {
        return null
    }
}

/**
 * Returns the value of a characteristic of a product.
 * @param {Object} product the product structure
 * @param {String} characteristic the characteristic name
 * @return null if characteristic doesn't exist, the value if length is 1 or all values
 */
export function valueOf(
    product: Product | ProductResult,
    characteristic: string
):
    | string
    | string[]
    | NumberValue
    | NumberValue[]
    | MediaValue
    | MediaValue[]
    | null {
    if (!product || !characteristic) {
        return null
    }

    const cv = isProduct(product)
        ? product.characteristicValues
        : product.product?.characteristicValues || null

    if (!cv || !Object.prototype.hasOwnProperty.call(cv, characteristic)) {
        return null
    }

    const values = cv[characteristic].values
    return values.length === 1 ? values[0] : values
}

function getPossibleValuesKey(value: any): string {
    if (typeof value === "string") {
        return value
    }

    if ("possibleValueId" in value) {
        return value.possibleValueId
    }

    return ""
}

/**
 * Returns the display value of a characteristic of a product.
 * @param {Object} product the product structure
 * @param {String} characteristic the characteristic name
 * @param {String} separator (optional) specifies the used separator, e.g. ", " or ";"
 * @return null if characteristic doesn't exist, the value if length is 1 or all values
 */
export function displayValueOf(
    product: Product | ProductResult,
    characteristic: string,
    separator = ","
):
    | PossibleValue
    | PossibleValue[]
    | string
    | NumberValue
    | MediaValue
    | Interval
    | number
    | null
    | undefined {
    if (!product || !characteristic) {
        return null
    }

    const cv = !isProduct(product) ? product.product.characteristicValues : null

    if (!cv || !Object.prototype.hasOwnProperty.call(cv, characteristic)) {
        return null
    }

    const values = cv[characteristic].values

    const characteristicDefinition = !isProduct(product)
        ? product.characteristics.find((c) => c.id === characteristic)
        : null

    if (characteristicDefinition && characteristicDefinition.possibleValues) {
        const mappedValues = values.map((value) => {
            const key = getPossibleValuesKey(value)

            const possibleValue =
                characteristicDefinition.possibleValues &&
                characteristicDefinition.possibleValues[key]

            if (
                possibleValue &&
                possibleValue.value &&
                typeof possibleValue.value !== "string"
            ) {
                return possibleValue.value.value
            } else {
                return possibleValue?.value
            }
        })

        return mappedValues.length === 1
            ? mappedValues[0]
            : mappedValues.join(separator)
    }
    return values.length === 1 ? values[0] : values.join(separator)
}

function getUri(mediaValue: any): string | null {
    if (typeof mediaValue?.uri === "string") {
        return mediaValue.uri
    }

    return null
}

/**
 * Extract the product image from a product.
 * @param {Object} product the product structure
 * @return imager uri or null
 */
export function productImage(product: Product | ProductResult): string | null {
    const img =
        valueOf(product, "product_image") || valueOf(product, "Product_image")

    return getUri(img)
}
